import type { UnitTypes } from 'unitData/types';
import type { CrusherJawCJInfoDataInterface } from 'unitData/Crushers/CrusherJawCJ/CrusherJawCJTypes';
import type { MobileHSIUnitInfoDataInterface } from 'unitData/Mobile/HSI/unitInfoData';
import type { MobileConeUnitInfoDataInterface } from 'unitData/Mobile/Cone/unitInfoData';
import type { MobileScreenUnitInfoDataInterface } from 'unitData/Mobile/Screen/unitInfoData';
import type { MobileJawUnitInfoDataInterface } from 'unitData/Mobile/Jaw/unitInfoData';
import type { BananaScreenUnitInfoDataInterface } from 'unitData/BananaScreen/unitInfoData';
import type { SchenckScreenUnitInfoDataMapInterface } from 'unitData/SchenckScreen/unitInfoData';
import type { CrusherVSIUnitInfoDataInterface } from 'unitData/Crushers/CrusherVSI/unitInfoData';
import type { FeedUnitInfoDataInterface } from 'unitData/Feed/unitInfoData';
import type { ImpactCrusherCIUnitInfoDataMapInterface } from 'unitData/Crushers/ImpactCrusherCI/unitInfoData';
import type { GyratoryCrusherCGUnitInfoDataInterface } from 'unitData/Crushers/GyratoryCrusherCG/unitInfoData';
import type { FreeFallScreenSSUnitInfoDataInterface } from 'unitData/FreeFallScreenSS/unitInfoData';
import type { FreeFallScreenSFUnitInfoDataInterface } from 'unitData/FreeFallScreenSF/unitInfoData';
import type { ConeCrusherCSUnitInfoDataMapInterface } from 'unitData/Crushers/ConeCrusherCS/unitInfoData';
import type { FeederAndScreenStUnitInfoDataMapInterface } from 'unitData/Feeders/FeederScreen/unitInfoData';
import type { PanFeederSPUnitInfoDataMapInterface } from 'unitData/Feeders/Pan/unitInfoData';
import type { PlateFeederSHUnitInfoDataMapInterface } from 'unitData/Feeders/Plate/unitInfoData';
import type { GrizzlyFeederUnitInfoDataMapInterface } from 'unitData/Feeders/Grizzly/unitInfoData';
import type { ConeCrusherCHUnitInfoDataMapInterface } from 'unitData/Crushers/CrusherConeCH/unitInfoData';
import type { GrizzlyScreenUnitInfoDataInterface } from 'unitData/GrizzlyScreenSG/unitInfoData';
import type { HybridCrusherCRUnitInfoDataInterface } from 'unitData/Crushers/HybridCrusherCR/unitInfoData';
import type { MobileVSIUnitInfoDataInterface } from 'unitData/Mobile/VSI/unitInfoData';
import type { MobileJawAndConeUnitInfoDataInterface } from 'unitData/Mobile/JawAndCone/unitInfoData';
import type { KwataniScreenUnitInfoDataMapInterface } from 'unitData/KwataniScreen/unitInfoData';
import type { BreakerUnitInfoDataInterface } from 'unitData/Breaker/BreakerTypes';

export const GET_UNIT_INFO_DATA_REQUEST = 'GET_UNIT_INFO_DATA_REQUEST';

export interface UnitInfoDataReducerInterface {
  unitInfoData: UnitInfoDataInterface;
}

export interface UnitInfoDataInterface {
  [UnitTypes.CONE_CRUSHER_CH]: ConeCrusherCHUnitInfoDataMapInterface;
  [UnitTypes.MOBILE_JAW_COMMON]: MobileJawUnitInfoDataInterface;
  [UnitTypes.MOBILE_SCREEN_COMMON]: MobileScreenUnitInfoDataInterface;
  [UnitTypes.MOBILE_HSI_COMMON]: MobileHSIUnitInfoDataInterface;
  [UnitTypes.MOBILE_VSI]: MobileVSIUnitInfoDataInterface;
  [UnitTypes.MOBILE_CONE_COMMON]: MobileConeUnitInfoDataInterface;
  [UnitTypes.CONE_CRUSHER_CS]: ConeCrusherCSUnitInfoDataMapInterface;
  [UnitTypes.HYBRID_CRUSHER_CR]: HybridCrusherCRUnitInfoDataInterface;
  [UnitTypes.FEEDER_AND_SCREEN]: FeederAndScreenStUnitInfoDataMapInterface;
  [UnitTypes.PAN_FEEDER_SP]: PanFeederSPUnitInfoDataMapInterface;
  [UnitTypes.PLATE_FEEDER_SH]: PlateFeederSHUnitInfoDataMapInterface;
  [UnitTypes.GRIZZLY_FEEDER_GENERAL]: GrizzlyFeederUnitInfoDataMapInterface;
  [UnitTypes.FEED]: FeedUnitInfoDataInterface;
  [UnitTypes.FREEFALL_SCREEN_SF]: FreeFallScreenSFUnitInfoDataInterface;
  [UnitTypes.FREEFALL_SCREEN_SS]: FreeFallScreenSSUnitInfoDataInterface;
  [UnitTypes.GRIZZLY_SCREEN]: GrizzlyScreenUnitInfoDataInterface;
  [UnitTypes.GYRATORY_CRUSHER_CG]: GyratoryCrusherCGUnitInfoDataInterface;
  [UnitTypes.IMPACT_CRUSHER_CI]: ImpactCrusherCIUnitInfoDataMapInterface;
  [UnitTypes.IMPACT_CRUSHER_CV]: CrusherVSIUnitInfoDataInterface;
  [UnitTypes.BANANA_SCREEN]: BananaScreenUnitInfoDataInterface;
  [UnitTypes.SCHENCK_SCREEN]: SchenckScreenUnitInfoDataMapInterface;
  [UnitTypes.KWATANI_SCREEN]: KwataniScreenUnitInfoDataMapInterface;
  [UnitTypes.JAW_CRUSHER_CJ]: CrusherJawCJInfoDataInterface;
  [UnitTypes.MOBILE_JAW_AND_CONE]: MobileJawAndConeUnitInfoDataInterface;
  [UnitTypes.CONVENTIONAL_SCREEN]: ConventionalScreenUnitInfoDataInterface;
  [UnitTypes.MULTISLOPE_SCREEN]: MultislopeScreenUnitInfoDataInterface;
  [UnitTypes.BREAKER]: BreakerUnitInfoDataInterface;
  SCREENING_MEDIA?: ScreeningMediaUnitInfoDataInterface;
}

export interface ScreeningMediaDetailsInterface {
  screeningMediaId?: string;
  model?: string;
  material?: string;
  availableTypes?: string[];
  sepSizeRange?: number[];
  maxFeedSizeRange?: number[];
  detailedMaxFeedSizeRange?: unknown[];
  singleSepSize?: number;
  singleFeedSize?: number;
  description?: string;
  type?: string;
  function?: string;
}

export type ScreeningMediaUnitInfoDataInterface = Record<
  string,
  ScreeningMediaDetailsInterface
>;

export interface ScreenSizeDetailsInterface {
  width: number;
  length: number;
  weight: number;
  motor?: number;
  maxGForce?: number;
}

interface ScreenFamilyDataInterface {
  minStroke: number;
  maxStroke: number;
  minSpeed: number;
  maxSpeed: number;
  cocSpeed: number;
  maxGForce?: number;
  models: { [modelName: string]: ScreenModelDetailsInterface };
}

export type ScreenSizeOption = string;

export interface ScreenModelDetailsInterface {
  sizes: Record<ScreenSizeOption, ScreenSizeDetailsInterface>;
}

export interface ConventionalScreenUnitInfoDataInterface {
  SJ?: ScreenFamilyDataInterface;
  SL?: ScreenFamilyDataInterface;
  KL?: ScreenFamilyDataInterface;
  SA?: ScreenFamilyDataInterface;
  SL_EXCITER?: ScreenFamilyDataInterface;
  SA_LM?: ScreenFamilyDataInterface;
}

export interface MultislopeScreenUnitInfoDataInterface {
  SM?: ScreenFamilyDataInterface;
}
